import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import PaginationControls from '../../utils/PaginationControls';
import DocumentPreview from '../../utils/DocumentPreview';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const ContextMappingDocSelector = ({
    fabricId,
    templateId,
    onClose,
    onMappingStarted,
    hasPendingChanges = false
}) => {
    const navigate = useNavigate();

    const API_BASE_URL = '/pceapi/genbi';
    const headers = {
        Authorization: GetCookie('access_token'),
    };

    // Threads + docs state
    const [threads, setThreads] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const [expandedThreads, setExpandedThreads] = useState({});
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [selectedDocName, setSelectedDocName] = useState(null);

    // Preview
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDocId, setPreviewDocId] = useState(null);
    const [previewDocumentName, setPreviewDocumentName] = useState(null);

    // UI flags
    const [isLoading, setIsLoading] = useState(false);
    const [isMapping, setIsMapping] = useState(false);

    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const closeErrorPopup = () => {
        setIsErrorPopupVisible(false);
        setErrorMessage('');
    };

    const closeSuccessPopup = () => {
        setIsSuccessPopupVisible(false);
        setSuccessMessage('');
    };

    // Only allow PDF and DOCX documents for mapping
    const isSupportedMappingDoc = (doc) => {
        if (!doc || !doc.document_name) {
            return false;
        }

        const name = String(doc.document_name).toLowerCase().trim();
        return name.endsWith('.pdf') || name.endsWith('.docx') || name.endsWith('.doc');
    };

    // Reset & load when fabric changes or when viewer is opened with a fabric
    useEffect(() => {
        if (fabricId) {
            setPage(1);
            setSize(25);
            setSearch('');
            setSearchInput('');
            setSelectedDocId(null);
            setSelectedDocName(null);
            setExpandedThreads({});
            fetchThreads(1, 25, '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fabricId]);

    // Fetch threads tree
    const fetchThreads = async (
        pageNumber = page,
        pageSize = size,
        searchText = search
    ) => {
        if (!fabricId) return;

        setIsLoading(true);
        try {
            const body = {
                page: pageNumber,
                size: pageSize,
                search: searchText || undefined
            };

            const response = await axios.post(
                `${API_BASE_URL}/threads-tree`,
                body,
                { headers }
            );

            const data = response.data;

            if (data.status === 'NotAuthorized') {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (data.status === 'failed') {
                setErrorMessage(data.message || 'Failed to load Data folders');
                setIsErrorPopupVisible(true);
                return;
            }

            setThreads(data.data || []);
            setTotal(data.total || 0);
            setPage(data.page || pageNumber);
            setSize(data.size || pageSize);
        } catch (error) {
            console.error('Error fetching Data folders tree:', error);
            setErrorMessage(error.message || 'Failed to load Data folders');
            setIsErrorPopupVisible(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Search handlers
    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const handleSearch = () => {
        const trimmed = (searchInput || '').trim();
        setSearch(trimmed);
        setPage(1);
        fetchThreads(1, size, trimmed);
    };

    // Pagination
    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(1);
        fetchThreads(1, newSize, search);
    };

    const handlePageChange = (direction) => {
        const totalPages = Math.max(1, Math.ceil(total / size));
        let newPage = page;

        if (direction === 'prev') {
            newPage = Math.max(1, page - 1);
        } else if (direction === 'next') {
            newPage = Math.min(totalPages, page + 1);
        }

        if (newPage !== page) {
            setPage(newPage);
            fetchThreads(newPage, size, search);
        }
    };

    // Expand/collapse threads
    const toggleThreadExpansion = (threadId) => {
        setExpandedThreads((prev) => ({
            ...prev,
            [threadId]: !prev[threadId]
        }));
    };

    // Doc selection
    const handleSelectDoc = (doc /* , thread */) => {
        setSelectedDocId(doc.id);
        setSelectedDocName(doc.document_name);
    };

    // Preview
    const openPreview = (doc) => {
        setPreviewDocId(doc.id);
        setPreviewDocumentName(doc.document_name);
        setPreviewOpen(true);
    };

    const closePreview = () => {
        setPreviewOpen(false);
        setPreviewDocId(null);
        setPreviewDocumentName(null);
    };

    // Map click
    const handleMap = async () => {
        if (!selectedDocId) {
            setErrorMessage('Please select a document to map.');
            setIsErrorPopupVisible(true);
            return;
        }

        if (!templateId) {
            setErrorMessage('Preset information is missing.');
            setIsErrorPopupVisible(true);
            return;
        }

        setIsMapping(true);
        try {
            const body = {
                template_id: templateId,
                doc_id: selectedDocId
            };

            const response = await axios.post(
                `${API_BASE_URL}/map-agent-context`,
                body,
                { headers }
            );

            const data = response.data;

            if (data.status === 'NotAuthorized') {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (data.status === 'failed') {
                setErrorMessage(
                    data.message || 'Failed to start agent context mapping'
                );
                setIsErrorPopupVisible(true);
                return;
            }

            // Success: backend has started mapping job (status RUNNING)
            setSuccessMessage(
                data.message || 'Agent context mapping started successfully'
            );
            setIsSuccessPopupVisible(true);

            if (onMappingStarted) {
                onMappingStarted();
            }

            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error('Error starting agent context mapping:', error);
            setErrorMessage(
                error.message || 'Failed to start agent context mapping'
            );
            setIsErrorPopupVisible(true);
        } finally {
            setIsMapping(false);
        }
    };

    if (!fabricId) return null;

    const totalPages = Math.max(1, Math.ceil(total / size));

    return (
        <>
            <div className="ev-card-4 ev-padding-small ev-radius-medium max-width-setting">
                {/* Mapping runs on the saved template, so unsaved edits are not included */}
                {hasPendingChanges && (
                    <div className="ev-alert-card-warning mb-2 py-1">
                        <p className="mb-0 ev-fs-11 text-truncate" title="Unsaved changes won't be considered. Click Save Preset first.">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Unsaved changes won't be considered. Click <b>Save Preset</b> first.
                        </p>
                    </div>
                )}
                {/* Info banner */}
                <div className="ev-alert-card-warning mb-2">
                    <p className="mb-0 ">
                        Select example file (.pdf/.docx) from Data folder list below to map the agent context.
                        This action cannot be undone.
                    </p>
                </div>

                {/* Search + pagination */}
                <div className="row mb-2">
                    {/* Search */}
                    <div className="col-5">
                        <div className="position-relative">
                            <button
                                type="button"
                                className="ev-btn-lightblue-fill-round search-btn-inside"
                                onClick={handleSearch}
                            >
                             <i className="bi bi-search"></i>
                             </button>
                            <input
                                type="text"
                                className="form-control search-input ms-1"
                                placeholder="Search Data folders"
                                value={searchInput}
                                onChange={handleSearchInputChange}
                                onKeyPress={handleSearchKeyPress}
                            />
                            
                               
                            
                        </div>
                    </div>

                    {/* Pagination controls */}
                    <div className="col-7 d-flex justify-content-end align-items-center">
                        <div className="d-flex align-items-center">
                            <button
                                type="button"
                                className="ev-btn-light-fill ev-btn-xsmall"
                                disabled={page <= 1}
                                onClick={() => handlePageChange('prev')}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <span className="ev-xsmall-p-dark mx-2">
                                Page {page}/{totalPages}
                            </span>
                            <button
                                type="button"
                                className="ev-btn-light-fill ev-btn-xsmall"
                                disabled={page >= totalPages}
                                onClick={() => handlePageChange('next')}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>

                        <div className="d-flex align-items-center ms-2">
                            <PaginationControls
                                initialSize={size}
                                onSizeChange={handleSizeChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Threads & docs list */}
                <div className="scrollable-element vh-25 hr-scroll-elem">
                    {isLoading ? (
                        <div className="text-center p-3 mt-3">
                            <div
                                className="spinner-border spinner-border-sm text-primary"
                                role="status"
                            >
                            </div>
                        </div>
                    ) : threads.length === 0 ? (
                        <p className="ev-small-p-blue text-center mt-2">
                            No Data folders found
                        </p>
                    ) : (
                        <div className="thread-tree">
                            {threads.map((thread) => {
                                // sirf supported docs ka count
                                const supportedDocs = (thread.documents || []).filter((doc) =>
                                    isSupportedMappingDoc(doc)
                                );
                                const supportedDocCount = supportedDocs.length;

                                return (
                                    <div key={thread.id} className="mb-2">
                                        <div className="d-flex align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-sm ev-btn-text-only-fill"
                                                onClick={() => toggleThreadExpansion(thread.id)}
                                            >
                                                <i
                                                    className={`bi ${expandedThreads[thread.id]
                                                            ? 'bi-chevron-down'
                                                            : 'bi-chevron-right'
                                                        }`}
                                                ></i>
                                            </button>

                                            <div
                                                onClick={() => toggleThreadExpansion(thread.id)}
                                                className="cursor-pointer"
                                            >
                                                <span className="ev-small-p-blue avatar-tooltip-bottom" data-tooltip={thread.name}>
                                                    {thread.name.length > 40 ? thread.name.substring(0, 40) + '...' : thread.name}
                                                </span>
                                                &nbsp;
                                                <span
                                                    className={`badge ev-btn-small ${thread.thread_access_level === 'PUBLIC'
                                                            ? 'bg-primary'
                                                            : 'bg-danger'
                                                        }`}
                                                >
                                                    {thread.thread_access_level}
                                                </span>
                                                <span className="ev-message-card1 ms-1 ev-btn-xsmall ev-fs-11">
                                                    {supportedDocCount} docs
                                                </span>
                                            </div>
                                        </div>


                                        {expandedThreads[thread.id] && supportedDocCount > 0 && (
                                            <div className="ms-4 mt-0">
                                                {supportedDocs.map((doc) => (
                                                    <div key={doc.id} className="mb-1 mt-1">
                                                        {/* Whole row */}
                                                        <div className="d-flex align-items-start">
                                                            {/* Radio */}
                                                            <input
                                                                type="radio"
                                                                className="form-check-input me-2 flex-shrink-0"
                                                                name="selectedMappingDoc"
                                                                checked={selectedDocId === doc.id}
                                                                onChange={() => handleSelectDoc(doc, thread)}
                                                            />

                                                            {/* Text + button together, left aligned */}
                                                            <div className="d-flex align-items-start flex-nowrap">
                                                                <span className="ev-xsmall-p-dark avatar-tooltip-bottom" data-tooltip={doc.document_name}>
                                                                    {doc.document_name.length > 40 ? doc.document_name.substring(0, 40) + '...' : doc.document_name}
                                                                </span>

                                                                <button
                                                                    type="button"
                                                                    className="ev-btn-lightblue-fill-round ev-btn-xsmall ms-2 flex-shrink-0"
                                                                    onClick={() => openPreview(doc)}
                                                                >
                                                                    <i className="bi bi-eye"></i> view
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>


                {/* Footer */}
                <div className="d-flex justify-content-end mt-1">
                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-bold-reg"
                        onClick={handleMap}
                        disabled={!selectedDocId || isMapping}
                    >
                        {isMapping ? 'Mapping…' : 'Map'}
                    </button>
                    <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-bold-reg  ms-2"
                        onClick={onClose}
                        disabled={isMapping}
                    >
                        Cancel
                    </button>

                </div>
            </div>

            {/* Preview & popups */}
            <DocumentPreview
                isOpen={previewOpen}
                onClose={closePreview}
                docId={previewDocId}
                documentName={previewDocumentName}
            />

            {isErrorPopupVisible && (
                <ErrorPopup onClose={closeErrorPopup} message={errorMessage} />
            )}

            {isSuccessPopupVisible && (
                <SuccessPopup onClose={closeSuccessPopup} message={successMessage} />
            )}
        </>
    );
};

export default ContextMappingDocSelector;